import { toast } from 'react-toastify';
import appApi from '../../../../../../api/app-api';
import { displayErrorText } from '../../../../../../api/errors/api-error-codes';
import { AccountingDepositDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-details-entity';
import { AccountingDepositEntity } from '../../../../../../api/types/accounting/accounting-deposit-entity';
import { AccountingDepositPaymentDetailsEntity } from '../../../../../../api/types/accounting/accounting-deposit-payment-details-entity';
import { AccountingDepositPaymentEntity } from '../../../../../../api/types/accounting/accounting-deposit-payment-entity';
import { DepositPaymentType } from '../../../../../../api/types/accounting/deposit-payment-type';
import generateAndDownloadExcelFile from '../../../../../../component/excel/generate-and-download-excel-file';
import { formatDate } from '../../../../../../utils/date-format';

// Aligned with `export-deposit-to-excel-operation.ts`: large enough to cover
// any realistic deposit since the backend paginates server-side.
const EXPORT_LIMIT = 100000;

const DEPOSITS_EXPORT_HEADERS = [
  'מספר הפקדה',
  'מזהה הפקדה',
  'תאריך הפקדה',
  'חשבון בנק',
  'נוצר ע״י',
  'תאריך יצירת הפקדה',
  'הערות',
  'מזהה תשלום',
  'סוג תשלום',
  'מקור',
  'מזהה מקור',
  'סכום',
  'תאריך הפקדה (לפי תשלום)',
  'תאריך יצירת תשלום',
] as const;

// Index of the per-payment "סכום" column — used for the bottom total row.
const AMOUNT_COLUMN_INDEX = DEPOSITS_EXPORT_HEADERS.indexOf('סכום');

// Match the single-deposit export so the row content here stays in lockstep
// with the one-deposit Excel that the dialog produces.
const paymentTypeLabel = (type: DepositPaymentType): string => {
  switch (type) {
    case DepositPaymentType.CASH:
      return 'מזומן';
    case DepositPaymentType.CHECK:
      return 'המחאה';
    default:
      return type;
  }
};

const paymentSourceLabel = (p: AccountingDepositPaymentEntity): string => {
  if (p.userPaymentId !== null) return 'תשלום סטודנט';
  if (p.userPaymentCheckDetailsId !== null) return 'המחאת סטודנט';
  if (p.crmPaymentId !== null) return 'תשלום CRM';
  if (p.crmCheckId !== null) return 'המחאה CRM';
  return '';
};

const paymentSourceIdLabel = (p: AccountingDepositPaymentEntity): string => {
  if (p.userPaymentId !== null) return String(p.userPaymentId);
  if (p.userPaymentCheckDetailsId !== null)
    return String(p.userPaymentCheckDetailsId);
  if (p.crmPaymentId !== null) return String(p.crmPaymentId);
  if (p.crmCheckId !== null) return String(p.crmCheckId);
  return '';
};

const bankAccountLabel = (
  deposit: AccountingDepositDetailsEntity | null,
): string => {
  if (!deposit) return '';
  const account = deposit.bankAccount;
  if (account) {
    const parts = [account.accountCode, account.accountName].filter(
      (v) => v && String(v).trim().length > 0,
    );
    if (parts.length > 0) return parts.join(' - ');
  }
  return deposit.bankAccountId !== null && deposit.bankAccountId !== undefined
    ? String(deposit.bankAccountId)
    : '';
};

// Build payment rows for one deposit. Pure function — accepts the deposit's
// "context" entity (so we can keep the `bankAccount` lookup that comes from the
// list response) plus the freshly loaded deposit + all its payments.
const buildDepositPaymentRows = (
  depositId: number,
  contextDeposit: AccountingDepositDetailsEntity | null,
  loadedDeposit: AccountingDepositEntity | null,
  payments: AccountingDepositPaymentDetailsEntity[],
): (string | number)[][] => {
  const depositForExport: AccountingDepositDetailsEntity | null = contextDeposit
    ? { ...contextDeposit, ...(loadedDeposit ?? {}) }
    : null;

  const depositId_ = depositForExport?.id ?? loadedDeposit?.id ?? depositId;
  const depositSerialNumber =
    depositForExport?.depositSerialNumber ?? loadedDeposit?.depositSerialNumber;
  const depositSerialNumberStr =
    typeof depositSerialNumber === 'number' ? depositSerialNumber : '';
  const depositDate =
    depositForExport?.depositDate ?? loadedDeposit?.depositDate;
  const depositDateStr = depositDate ? formatDate(depositDate as Date) : '';
  const depositCreatedAt =
    depositForExport?.createdAt ?? loadedDeposit?.createdAt;
  const depositCreatedAtStr = depositCreatedAt
    ? formatDate(depositCreatedAt as Date)
    : '';
  const bankAccountStr = bankAccountLabel(depositForExport);
  const createdByName = (
    depositForExport?.createdByName ??
    loadedDeposit?.createdByName ??
    ''
  ).trim();
  const notes =
    (depositForExport?.notes ?? loadedDeposit?.notes ?? '')?.trim() ?? '';

  const depositContextCells = [
    depositSerialNumberStr,
    depositId_,
    depositDateStr,
    bankAccountStr,
    createdByName,
    depositCreatedAtStr,
    notes,
  ];

  // Per-row "deposit date":
  //   - check rows → the check's clearing date (`checkDetails.dueDate`)
  //   - cash rows  → the deposit-level date (already formatted above)
  const perRowDepositDate = (
    p: AccountingDepositPaymentDetailsEntity,
  ): string => {
    if (p.paymentType === DepositPaymentType.CHECK) {
      return p.checkDetails?.dueDate
        ? formatDate(p.checkDetails.dueDate as Date)
        : '';
    }
    return depositDateStr;
  };

  // Per-row amount: cash rows → `cashAmount`, check rows → `checkDetails.amount`.
  const perRowAmount = (p: AccountingDepositPaymentDetailsEntity): string => {
    if (p.paymentType === DepositPaymentType.CASH) {
      return typeof p.cashAmount === 'number' ? p.cashAmount.toFixed(2) : '';
    }
    if (p.paymentType === DepositPaymentType.CHECK) {
      return typeof p.checkDetails?.amount === 'number'
        ? p.checkDetails.amount.toFixed(2)
        : '';
    }
    return '';
  };

  if (payments.length === 0) {
    return [[...depositContextCells, '', '', '', '', '', '', '']];
  }

  return payments.map((p) => [
    ...depositContextCells,
    p.id,
    paymentTypeLabel(p.paymentType),
    paymentSourceLabel(p),
    paymentSourceIdLabel(p),
    perRowAmount(p),
    perRowDepositDate(p),
    p.createdAt ? formatDate(p.createdAt as Date) : '',
  ]);
};

// Hard cap exposed so callers (the UI) can show a matching message when the
// user tries to select more than the supported batch size.
export const MAX_DEPOSITS_EXPORT_BATCH = 50;

/**
 * Fetches all payments for every deposit in `deposits` and bundles them into
 * a single .xlsx sheet. Each row mirrors the per-deposit export produced by
 * `exportDepositToExcelOperation`, with an added `מספר הפקדה` column.
 *
 * A deposit whose payment fetch fails is skipped (toast warn). If every
 * deposit fails the download is aborted with a toast error.
 */
export const exportDepositsToExcelOperation = async (
  deposits: AccountingDepositDetailsEntity[],
  setExporting: (exporting: boolean) => void,
): Promise<void> => {
  if (deposits.length === 0) {
    toast.warn('יש לבחור לפחות הפקדה אחת לייצוא');
    return;
  }

  if (deposits.length > MAX_DEPOSITS_EXPORT_BATCH) {
    toast.warn(`ניתן לייצא עד ${MAX_DEPOSITS_EXPORT_BATCH} הפקדות בלחיצה אחת`);
    return;
  }

  try {
    setExporting(true);

    // Fetch all selected deposits in parallel so the UX scales linearly with
    // the slowest single deposit instead of the sum of all of them.
    const fetches = await Promise.all(
      deposits.map(async (deposit) => {
        try {
          const res = await appApi.getAccountingDepositById(
            deposit.id,
            0,
            EXPORT_LIMIT,
          );
          if (res.data.errCode) {
            return {
              deposit,
              ok: false as const,
              errorText: displayErrorText(res.data.errCode),
            };
          }
          return {
            deposit,
            ok: true as const,
            loadedDeposit: res.data.body?.deposit ?? null,
            payments: res.data.body?.payments?.data ?? [],
          };
        } catch (e) {
          return { deposit, ok: false as const, errorText: null };
        }
      }),
    );

    const failed = fetches.filter((f) => !f.ok);
    const succeeded = fetches.filter(
      (f): f is Extract<typeof f, { ok: true }> => f.ok,
    );

    if (succeeded.length === 0) {
      // Surface the first errCode message if we have one, otherwise the
      // generic network message — matches the single-deposit operation.
      const firstApiError = failed.find(
        (f): f is Extract<typeof f, { ok: false }> =>
          !f.ok && typeof f.errorText === 'string' && f.errorText.length > 0,
      );
      if (firstApiError && firstApiError.errorText) {
        toast.warn(firstApiError.errorText);
      } else {
        toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
      }
      setExporting(false);
      return;
    }

    const allRows = succeeded.flatMap(({ deposit, loadedDeposit, payments }) =>
      buildDepositPaymentRows(deposit.id, deposit, loadedDeposit, payments),
    );

    // Sum each deposit once (not once per payment row).
    const depositsTotalAmount = succeeded.reduce(
      (sum, { deposit, loadedDeposit }) => {
        const amount =
          deposit.totalAmount ?? loadedDeposit?.totalAmount ?? null;
        return sum + (typeof amount === 'number' ? amount : 0);
      },
      0,
    );

    const totalRow: (string | number)[] = Array.from(
      { length: DEPOSITS_EXPORT_HEADERS.length },
      () => '',
    );
    totalRow[0] = 'סכום כולל ההפקדה';
    totalRow[AMOUNT_COLUMN_INDEX] = depositsTotalAmount.toFixed(2);

    const fileName =
      succeeded.length === 1
        ? `הפקדה ${succeeded[0].deposit.depositSerialNumber ?? succeeded[0].deposit.id}`
        : `הפקדות (${succeeded.length})`;

    generateAndDownloadExcelFile(
      [[...DEPOSITS_EXPORT_HEADERS], ...allRows, totalRow],
      {
        fileName,
        sheetName: 'הפקדות',
      },
    );

    if (failed.length > 0) {
      toast.warn(`ייצוא הושלם עם ${failed.length} הפקדות שלא נטענו בהצלחה`);
    }

    setExporting(false);
  } catch (e) {
    toast.error('משהו קרה, יש לנסות שוב בעוד מספר רגעים');
    setExporting(false);
  }
};
